import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { Header } from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import '../NoteCustomstyle.css';

//  import Material UI dropdown components
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const CostOptimizationHub = () => {
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100vh" }), []);
  const gridStyle = useMemo(() => ({ height: 'calc(-200px + 80vh)', width: "100%", marginTop: '50px' }), []);
  const [rowData, setRowData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState('');
  const [role, setRole] = useState('');
  const [tenantName, settenantName] = useState<string>(''); // dropdown state
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);
  const identityApi = useApi(identityApiRef)

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenants = await fetchTenantsFromDb();
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        setTenantsFromDb(tenants);
        const baseurl = await discoveryApi.getBaseUrl('user');
        let data: any[] = [];
        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/cost-optimization-hub`, {
            method: 'GET',
          });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }
          let response = await fetchresponse.json();
          data = response.data || response; // handle wrapper case
          if (response.role) setRole(response.role);
          console.log('Fetched data:', data);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }

        // Parse the recommendation details
        const recommendations = data;
        const formattedData = recommendations.flatMap((rec: any) => {
          try {
            const parsedDetails = JSON.parse(rec.recommendationDetails);
            return [{
              tenantName: rec.tenantName || 'N/A', // add tenantName for filtering
              accountId: parsedDetails.accountId,
              actionType: parsedDetails.actionType,
              currencyCode: parsedDetails.currencyCode,
              currentResourceSummary: parsedDetails.currentResourceSummary,
              currentResourceType: parsedDetails.currentResourceType,
              estimatedMonthlyCost: parsedDetails.estimatedMonthlyCost,
              estimatedMonthlySavings: parsedDetails.estimatedMonthlySavings,
              estimatedSavingsPercentage: parsedDetails.estimatedSavingsPercentage,
              implementationEffort: parsedDetails.implementationEffort,
              lastRefreshTimestamp: parsedDetails.lastRefreshTimestamp,
              recommendationId: parsedDetails.recommendationId,
              recommendationLookbackPeriodInDays: parsedDetails.recommendationLookbackPeriodInDays,
              recommendedResourceSummary: parsedDetails.recommendedResourceSummary,
              recommendedResourceType: parsedDetails.recommendedResourceType,
              region: parsedDetails.region,
              resourceArn: parsedDetails.resourceArn,
              resourceId: parsedDetails.resourceId,
              restartNeeded: parsedDetails.restartNeeded,
              rollbackPossible: parsedDetails.rollbackPossible,
              source: parsedDetails.source,
              tags: parsedDetails.tags?.join(', ') || ''
            }];
          } catch (error) {
            console.error('Error parsing recommendation details:', error);
            return [];
          }
        });

        setRowData(formattedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setLoading(false);
      }
    };

    fetchData();

    const orgName = localStorage.getItem('orgName');
    if (orgName) {
      setOrg(orgName);
    }
  }, [discoveryApi]);
  const currencyFormatter = (params: any) => {
    return params.value ? `$${params.value.toFixed(2)}` : '$0.00';
  };

  const percentageFormatter = (params: any) => {
    return params.value ? `${Math.round(params.value)}%` : '0%';
  };

  const columnDefs = useMemo(() => {
    const baseCols = [
    { headerName: "Tenant Name", field: "tenantName", headerTooltip: "Tenant Name" },
    { headerName: "Account Id", field: "accountId", headerTooltip: "Account Id" },
    { headerName: "Resource type", field: "recommendedResourceType", headerTooltip: "Recommended Resource Type" },
    { headerName: "Resource ID", field: "resourceId", headerTooltip: "Resource ID" },
    { headerName: "Top recommendation action", field: "actionType", headerTooltip: "Action Type" },
    { headerName: "Current resource summary", field: "currentResourceSummary", headerTooltip: "Current Resource Summary" },
    { headerName: "Recommended resource summary", field: "recommendedResourceSummary", headerTooltip: "Recommended Resource Summary" },
    { headerName: "Estimated monthly cost", field: "estimatedMonthlyCost", headerTooltip: "Estimated Monthly Cost", valueFormatter: currencyFormatter },
    { headerName: "Estimated monthly savings", field: "estimatedMonthlySavings", headerTooltip: "Estimated Monthly Savings", valueFormatter: currencyFormatter },
    { headerName: "Estimated savings percentage", field: "estimatedSavingsPercentage", headerTooltip: "Estimated Savings Percentage", valueFormatter: percentageFormatter },
    { headerName: "Implementation effort", field: "implementationEffort", headerTooltip: "Implementation Effort" },
    { headerName: "Is resource restart needed", field: "restartNeeded", headerTooltip: "Restart Needed", valueGetter: params => params.data.restartNeeded ? 'Yes' : 'No' },
    { headerName: "Is rollback possible", field: "rollbackPossible", headerTooltip: "Rollback Possible", valueGetter: params => params.data.rollbackPossible ? 'Yes' : 'No' },
  ];
  
  return baseCols.map(col =>
    col.field === "tenantName"
      ? { ...col, hide: role != "SuperAdmin", rowGroup: role != "SuperAdmin" ? false : col.rowGroup }
      : col
  );
}, [role]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1, filter: true, sortable: true, resizable: true, tooltipShowDelay: 0, tooltipInteraction: true, columnMenu: "new"
    };
  }, []);

  const gridOptions = { suppressContextMenu: true };

  //  filter data based on selected tenant
  const filteredData = tenantName
    ? rowData.filter(row => String(row.tenantName) === String(tenantName))
    : rowData;

  //  extract unique tenants
  const tenants = [...new Set(rowData.map(r => r.tenantName))];

  return (
    <div style={containerStyle}>
      <div>
        <Header title="Cost Optimization Hub" subtitle={`${org}`} />
      </div>

      {/* Dropdown only for SuperAdmin */}
      {role === "SuperAdmin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel></InputLabel>
            <Select
              value={tenantName}
              onChange={(e) => settenantName(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    // force vertical stacking
                    '& .MuiList-root': {
                      display: 'flex !important',
                      flexDirection: 'column !important',
                    },
                    // force left-aligned text inside items
                    '& .MuiMenuItem-root': {
                      justifyContent: 'flex-start',
                      pl: 2,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Tenants</MenuItem>
             {tenantsFromDb.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.name}>
                  {tenant.name}
                </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      )}

      {loading ? (<Loader />) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={{ ...gridStyle, marginTop: 10 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
            />
          </div>
          <div className="recommended-action-container">
            <div style={{ overflowY: 'scroll', width: '100%' }}>
              <strong>Recommended Action</strong>
              <ul>
                <li>
                  <strong>Enable Cost Optimization Hub in AWS</strong>
                  <p>Opt in using the AWS Management Console, API, AWS CLI, or SDKs to allow Cost Optimization Hub to import recommendations from AWS services.</p>
                  <ul>
                    <li>Select the account scope:</li>
                    <ul>
                      <li>This Account Only: Recommendations for the current account.</li>
                      <li>This Account and All Member Accounts: Recommendations for all member accounts if using AWS Organizations.</li>
                      <li>Management Account: Enable for the management account only or all member accounts.</li>
                    </ul>
                  </ul>
                  <p>Considerations:</p>
                  <ul>
                    <li>Ensure you have opted in to Compute Optimizer for rightsizing recommendations.</li>
                    <li>Recommendations may vary based on your account and usage patterns and will take up to 24 hours to generate.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          <div className="note-container">
            <p>
              <strong>Note*</strong><span className="note-text">: Estimated savings in AWS Cost Optimization Hub are calculated by analyzing AWS usage and applying optimization recommendations. This involves evaluating current resource usage, identifying underutilized or idle resources, and suggesting rightsizing, deletion, or more cost-effective purchasing options like Reserved Instances or Savings Plans. The Hub aggregates and deduplicates these recommendations, incorporating specific pricing terms and discounts to provide accurate savings estimates.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default CostOptimizationHub;
